/**
 * Frontend human-in-the-loop tool for presenting an option list.
 *
 * Registered via makeAssistantTool so assistant-ui:
 *   1. Sends the tool definition in RunAgentInput.tools
 *   2. Sets status to "requires-action" for human input
 *   3. Enables addResult() to send the user's selections back
 *
 * Supports single-select (radio-style) and multi-select (checkbox-style).
 */
import { useState, useMemo } from "react";
import { makeAssistantTool } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Check } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { ToolCard } from "./ToolCard";

interface OptionItem {
  id: string;
  label: string;
  description?: string;
}

interface OptionListArgs {
  title: string;
  description: string;
  options: string;
  allow_multiple: boolean;
  min_selections: number;
  max_selections: number;
}

function parseOptions(raw: unknown): OptionItem[] | null {
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(
      (item): item is OptionItem =>
        typeof item === "object" &&
        item !== null &&
        typeof item.id === "string" &&
        typeof item.label === "string"
    );
  } catch {
    return null;
  }
}

const OptionListRender: ToolCallMessagePartComponent<OptionListArgs, string> = ({
  args,
  result,
  status,
  addResult,
}) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const options = useMemo(() => (args ? parseOptions(args.options) : null), [args]);

  const isRunning = status.type === "running";
  const isSubmitted = result != null;
  const allowMultiple = args?.allow_multiple ?? false;
  const minSelections = args?.min_selections ?? 1;
  const maxSelections = args?.max_selections ?? (allowMultiple ? Infinity : 1);

  const handleToggle = (id: string) => {
    if (isSubmitted) return;

    setSelected((prev) => {
      const next = new Set(prev);
      if (allowMultiple) {
        if (next.has(id)) {
          next.delete(id);
        } else if (next.size < maxSelections) {
          next.add(id);
        }
      } else {
        next.clear();
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    if (!options || selected.size < minSelections) return;
    const selectedLabels = options.filter((opt) => selected.has(opt.id)).map((opt) => opt.label);
    addResult(
      `The user selected: ${selectedLabels.join(", ")}. Respond with text only. Do NOT call any tool.`
    );
  };

  // Submitted state — show selections as static display
  if (isSubmitted && options) {
    return (
      <ToolCard
        label="Option List"
        color="blue"
        isLoading={false}
        loadingText=""
        isSubmitted={true}
      >
        {args?.title && <p className="font-medium text-sm">{args.title}</p>}
        <div className="flex flex-col gap-1.5">
          {options.map((opt) => {
            const isSelected = selected.has(opt.id);
            return (
              <div
                key={opt.id}
                className={cn(
                  "flex items-center text-left text-sm rounded-lg border px-3 py-2",
                  isSelected
                    ? "border-blue-600 bg-blue-600 text-white font-medium"
                    : "border-gray-100 bg-gray-50/50 text-gray-300"
                )}
              >
                {isSelected && <Check className="size-3.5 mr-2 shrink-0" />}
                <span>{opt.label}</span>
              </div>
            );
          })}
        </div>
      </ToolCard>
    );
  }

  return (
    <ToolCard
      label="Option List"
      color="blue"
      isLoading={isRunning || !options}
      loadingText="Generating options..."
      isSubmitted={false}
    >
      {args?.title && <p className="font-medium text-sm">{args.title}</p>}
      {args?.description && <p className="text-xs text-muted-foreground">{args.description}</p>}

      <div className="flex flex-col gap-1.5">
        {options?.map((opt) => {
          const isSelected = selected.has(opt.id);
          return (
            <button
              key={opt.id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => handleToggle(opt.id)}
              className={cn(
                "flex items-start text-left text-sm rounded-lg border px-3 py-2.5 transition-all duration-150",
                isSelected
                  ? "border-blue-500 bg-blue-100 text-blue-800 font-medium"
                  : "border-blue-200 bg-blue-50/60 text-gray-800 hover:bg-blue-100 hover:border-blue-300 active:scale-[0.98] cursor-pointer"
              )}
            >
              <div
                className={cn(
                  "size-4 rounded border mr-2.5 mt-0.5 shrink-0 flex items-center justify-center",
                  allowMultiple ? "rounded" : "rounded-full",
                  isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300 bg-white"
                )}
              >
                {isSelected && <Check className="size-3 text-white" />}
              </div>
              <div>
                <span>{opt.label}</span>
                {opt.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <Button
        size="sm"
        disabled={selected.size < minSelections}
        onClick={handleConfirm}
        className="mt-1"
      >
        Confirm{allowMultiple && selected.size > 0 ? ` (${selected.size})` : ""}
      </Button>
    </ToolCard>
  );
};

// Intersection with Record<string, unknown> needed because makeAssistantTool's
// generic constraint requires an index signature that interfaces don't provide.
export const OptionListTool = makeAssistantTool<OptionListArgs & Record<string, unknown>, string>({
  toolName: "present_option_list",
  type: "human",
  description:
    "Present a list of options for the user to choose from. " +
    "Can be single-select or multi-select. The user confirms their selection.",
  parameters: {
    type: "object",
    properties: {
      title: { type: "string", description: "Heading for the option list" },
      description: {
        type: "string",
        description: "Optional context or instructions shown below the title",
      },
      options: {
        type: "string",
        description:
          'JSON array of option objects, each with: id (string), label (string), description (optional string). Example: [{"id":"a","label":"Option A","description":"Details about A"}]',
      },
      allow_multiple: {
        type: "boolean",
        description: "Whether the user can select multiple options (default false)",
      },
      min_selections: {
        type: "number",
        description: "Minimum number of selections required (default 1)",
      },
      max_selections: {
        type: "number",
        description:
          "Maximum number of selections allowed (default 1 for single-select, unlimited for multi-select)",
      },
    },
    required: ["title", "options"],
  },
  render: OptionListRender,
});
